import { useState } from "react";
import emailjs from "emailjs-com";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./Contact.module.css";
import { db } from "../../firebaseConfig";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaPaperPlane, FaCheck } from "react-icons/fa";

const Contact = () => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateMobile = (mobile) => /^\d{10}$/.test(mobile);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setMessage("");
    setLoading(true);

    const { fullName, email, mobile, message: userMessage } = form;

    if (!fullName || !email || !mobile || !userMessage.trim()) {
      const msg = "⚠️ Please fill in all the fields.";
      setStatus("error");
      setMessage(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!validateMobile(mobile)) {
      const msg = "⚠️ Enter a valid 10-digit mobile number.";
      setStatus("error");
      setMessage(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    // Load environment variables
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const confirmationTemplateID = process.env.REACT_APP_EMAILJS_CONFIRMATION_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceID || !templateID || !confirmationTemplateID || !publicKey) {
      const msg = "❌ Email service configuration error. Please try again later.";
      toast.error(msg);
      setStatus("error");
      setMessage(msg);
      setLoading(false);
      return;
    }

    try {
      const time = new Date().toLocaleString();

      // Try to save to Firestore (optional - can be removed if not needed)
      try {
        const docRef = await addDoc(collection(db, "contactMessages"), {
          fullName,
          email,
          mobile,
          message: userMessage,
          createdAt: Timestamp.now(),
        });
        console.log("✅ Message saved with ID:", docRef.id);
      } catch (firestoreError) {
        console.warn("⚠️ Could not save to database, continuing with email...", firestoreError);
        // Continue with email even if Firestore fails
      }

      // Send email to admin
      await emailjs.send(
        serviceID,
        templateID,
        {
          name: fullName,
          email,
          mobile,
          message: userMessage,
          time,
        },
        publicKey
      );

      // Send confirmation email to user
      await emailjs.send(
        serviceID,
        confirmationTemplateID,
        {
          to_name: fullName,
          to_email: email,
          name: fullName,
          email,
          mobile,
          message: userMessage,
          time,
        },
        publicKey
      );

      const successMsg = "✅ Thank you for contacting us. We will reach out to you shortly!";
      toast.success(successMsg);
      setStatus("success");
      setMessage(successMsg);
      setForm({ fullName: "", email: "", mobile: "", message: "" });

    } catch (err) {
      console.error("❌ Submission error:", err);
      
      let errorMsg = "❌ Failed to send message. Please try again or contact us directly.";
      
      if (err?.text?.includes("permissions")) {
        errorMsg = "❌ Service temporarily unavailable. Please contact us directly at contact@brandversetech.com";
      } else if (err?.message?.includes("Network Error")) {
        errorMsg = "❌ Network error. Please check your connection and try again.";
      }
      
      toast.error(errorMsg);
      setStatus("error");
      setMessage(errorMsg);
    }

    setLoading(false);
  };

  return (
    <motion.div 
      className={styles.contactContainer}
      id="contact-section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className={styles.backgroundPattern}></div>
      
      <div className={styles.headerSection}>
  <motion.h2 
    className={styles.title}
    initial={{ y: -20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 0.2, duration: 0.6 }}
  >
    Get in Touch
  </motion.h2>

        
        <motion.p 
          className={styles.subtitle}
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
         Have questions about our services or want to explore how we can support your business growth? Reach out to us today.
        </motion.p>
      </div>
      
      <div className={styles.contactLayout}>
        <motion.div 
          className={styles.contactCard}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <h3 className={styles.sectionTitle}>Send us a message</h3>

          <form onSubmit={handleSubmit} className={styles.form} id="contact-form">
            <motion.div 
              className={styles.formGroup}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <label htmlFor="fullName-input" className={styles.label}>Your Name</label>
              <input
                type="text"
                name="fullName"
                id="fullName-input"
                value={form.fullName}
                onChange={handleChange}
                className={styles.input}
                placeholder="Enter your name"
                required
              />
            </motion.div>

            <motion.div 
              className={styles.formGroup}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <label htmlFor="email-input" className={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                id="email-input"
                value={form.email}
                onChange={handleChange}
                className={styles.input}
                placeholder="Enter your email"
                required
              />
            </motion.div>

            <motion.div 
              className={styles.formGroup}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
            >
              <label htmlFor="mobile-input" className={styles.label}>Mobile Number</label>
              <input
                type="tel"
                name="mobile"
                id="mobile-input"
                value={form.mobile}
                onChange={handleChange}
                className={styles.input}
                placeholder="Enter your mobile number"
                required
                pattern="\d{10}"
                title="Enter a valid 10-digit mobile number"
              />
            </motion.div>

            <motion.div 
              className={styles.formGroup}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.5 }}
            >
              <label htmlFor="message-input" className={styles.label}>Message</label>
              <textarea
                name="message"
                id="message-input"
                value={form.message}
                onChange={handleChange}
                className={styles.textarea}
                placeholder="Enter your message"
                required
                rows="5"
              ></textarea>
            </motion.div>

            <motion.button 
              type="submit" 
              className={`${styles.submitButton} ${loading ? styles.loading : ''} ${status === 'success' ? styles.success : ''}`}
              id="submit-button"
              disabled={loading}
              whileHover={!loading ? { scale: 1.02, boxShadow: "0 8px 25px rgba(176, 139, 53, 0.3)" } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.5 }}
            >
              {loading ? (
                <div className={styles.buttonContent}>
                  <span className={styles.buttonLoader}></span>
                  <span>Sending...</span>
                </div>
              ) : status === 'success' ? (
                <div className={styles.buttonContent}>
                  <FaCheck className={styles.buttonIcon} />
                  <span>Message Sent!</span>
                </div>
              ) : (
                <div className={styles.buttonContent}>
                  <FaPaperPlane className={styles.buttonIcon} />
                  <span>Send Message</span>
                </div>
              )}
            </motion.button>
          </form>

          <AnimatePresence>
            {status && (
              <motion.p 
                className={`${styles.statusMessage} ${status === "error" ? styles.error : styles.success}`}
                id="status-message"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {message}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div 
          className={styles.contactInfo}
          id="contact-info-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <h3 className={styles.sectionTitle}>Contact Information</h3>
          
          <ul className={styles.contactList}>
            <li className={styles.contactItem} id="email-contact">
              <div className={styles.contactIcon}>
                <FaEnvelope />
              </div>
              <div className={styles.contactDetails}>
                <span className={styles.contactLabel}>Email</span>
                <a href="mailto:contact@brandversetech.com" className={styles.contactValue}>
                  contact@brandversetech.com
                </a>
              </div>
            </li>
            <li className={styles.contactItem} id="phone-contact">
              <div className={styles.contactIcon}>
                <FaPhone />
              </div>
              <div className={styles.contactDetails}>
                <span className={styles.contactLabel}>Phone</span>
                <a href="tel:+919154884214" className={styles.contactValue}>
                  +91 91548 84214
                </a>
              </div>
            </li>
            <li className={styles.contactItem} id="address-contact">
              <div className={styles.contactIcon}>
                <FaMapMarkerAlt />
              </div>
              <div className={styles.contactDetails}>
                <span className={styles.contactLabel}>Office</span>
                <span className={styles.contactValue}>
                  Tadepalli, Vijayawada - 522502<br />
                  Andhra Pradesh
                </span>
              </div>
            </li>
          </ul>

          <div className={styles.operatingHours} id="operating-hours">
            <h4 className={styles.operatingTitle}>
              <FaClock className={styles.clockIcon} />
              Operating Hours
            </h4>
            <ul className={styles.hoursList}>
              <li className={styles.hoursItem}>Monday - Friday: 9:00 AM - 6:00 PM</li>
              <li className={styles.hoursItem}>Saturday: 9:00 AM - 1:00 PM</li>
              <li className={styles.hoursItem}>Sunday: Closed</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Contact;